// The visible affordance for the in-app HSHM Rule Book reader. Renders
// a vertical "MANUAL" tab pinned to the right edge of the viewport
// (middle), owns the drawer's open state, and mounts ManualDrawer
// when clicked. Self-contained — drop it anywhere that's mounted in
// the root render tree and it works.
//
// The handle hides itself while the drawer is open so it doesn't sit
// behind the slide-over panel. The drawer's own close affordances
// (close button, Esc, backdrop click) flip the state back.

import React, { useEffect, useState } from 'react';

import ManualDrawer from './ManualDrawer';
import { MANUAL_TEXT } from './_manual.generated';
import { parseHeadings } from './manualToc';
import { OPEN_MANUAL_SECTION_EVENT } from '../Insights/InsightsWindow';
import './ManualLauncher.css';


// Resolve a manual section reference ('§7.2.4' or '7.2.4') to the
// heading slug the reader stamped. parseHeadings runs the same
// github-slugger pass as ManualReader, so the slug it returns matches
// a heading id that exists in the rendered manual. Returns null when no
// heading matches that section number (e.g. sections without their own
// heading) — the drawer then opens to the top.
function sectionToSlug(section) {
    if (!section) {
        return null;
    }
    const sectionNumber = String(section).replace(/^§/, '').trim();
    if (!sectionNumber) {
        return null;
    }
    // Match the heading whose leading number is exactly this section —
    // the next char must be whitespace/colon/end so '7.2.2' doesn't
    // swallow '7.2.2.1'.
    const escaped = sectionNumber.replace(/\./g, '\\.');
    const matcher = new RegExp('^' + escaped + '(?:[:\\s]|$)');
    const headings = parseHeadings(MANUAL_TEXT);
    const match = headings.find(
        (heading) => matcher.test(heading.text.trim())
    );
    return match ? match.slug : null;
}


function ManualLauncher() {
    const [open, setOpen] = useState(false);
    const [initialSlug, setInitialSlug] = useState(null);

    // The Insights Window's manual anchor dispatches this event; open
    // the drawer to the cited section.
    useEffect(() => {
        function handleOpenSection(event) {
            const section = event.detail && event.detail.section;
            setInitialSlug(sectionToSlug(section));
            setOpen(true);
        }
        window.addEventListener(OPEN_MANUAL_SECTION_EVENT, handleOpenSection);
        return () => {
            window.removeEventListener(
                OPEN_MANUAL_SECTION_EVENT, handleOpenSection);
        };
    }, []);

    return (
        <React.Fragment>
            {!open && (
                <button
                    type="button"
                    className="manual-launcher-handle"
                    aria-label="Open HSHM Rule Book"
                    onClick={() => {
                        setInitialSlug(null);
                        setOpen(true);
                    }}
                >
                    <span className="manual-launcher-handle-text">
                        MANUAL
                    </span>
                </button>
            )}
            <ManualDrawer
                open={open}
                initialSlug={initialSlug}
                onClose={() => setOpen(false)}
            />
        </React.Fragment>
    );
}

export default ManualLauncher;
